import { useEffect, useRef, useState } from "react";
import {
  Link,
  useNavigate,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowRight,
  LogOut,
  Heart,
  Package,
  UserRound,
  MapPin,
} from "lucide-react";
import { apiFetch, mainImage, money } from "../lib/api";
import AddressForm from "../components/AddressForm";

/* =========================================================
   GOOGLE AUTHENTICATION
========================================================= */

export function startGoogleLogin() {
  window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/google/login`;
}

export function GoogleButton({
  label = "Continue with Google",
}) {
  return (
    <button
      type="button"
      className="social-button social-button-google"
      onClick={startGoogleLogin}
    >
      <span className="social-button-icon" aria-hidden="true">
        <svg
          viewBox="0 0 24 24"
          width="18"
          height="18"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill="#4285F4"
            d="M21.35 12.23c0-.7-.06-1.38-.18-2.03H12v3.84h5.23a4.47 4.47 0 0 1-1.94 2.93v2.44h3.14c1.84-1.7 2.92-4.2 2.92-7.18Z"
          />
          <path
            fill="#34A853"
            d="M12 21.6c2.63 0 4.84-.87 6.45-2.35l-3.14-2.44c-.87.58-1.98.92-3.31.92-2.55 0-4.72-1.72-5.5-4.04H3.26v2.52A9.74 9.74 0 0 0 12 21.6Z"
          />
          <path
            fill="#FBBC05"
            d="M6.5 13.69a5.86 5.86 0 0 1 0-3.38V7.79H3.26a9.6 9.6 0 0 0 0 8.42l3.24-2.52Z"
          />
          <path
            fill="#EA4335"
            d="M12 6.27c1.43 0 2.72.49 3.74 1.46l2.8-2.8C16.84 3.37 14.63 2.4 12 2.4a9.74 9.74 0 0 0-8.74 5.39L6.5 10.31c.78-2.32 2.95-4.04 5.5-4.04Z"
          />
        </svg>
      </span>

      <span>{label}</span>
    </button>
  );
}

/* =========================================================
   FACEBOOK AUTHENTICATION
========================================================= */

export function FacebookButton({
  label = "Continue with Facebook",
}) {
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let mounted = true;

    apiFetch("/auth/facebook/available")
      .then((data) => {
        if (mounted) {
          setEnabled(!!data.enabled);
        }
      })
      .catch(() => {
        if (mounted) {
          setEnabled(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, []);

  if (!enabled) {
    return null;
  }

  const start = async () => {
    if (busy) {
      return;
    }

    setBusy(true);

    try {
      const data = await apiFetch("/auth/facebook/login");

      if (!data?.authorize_url) {
        throw new Error("Facebook login is unavailable");
      }

      window.location.href = data.authorize_url;
    } catch (err) {
      setBusy(false);
      toast.error(
        err.message || "Facebook login unavailable"
      );
    }
  };

  return (
    <button
      type="button"
      className="social-button"
      onClick={start}
      disabled={busy}
    >
      {busy ? "Connecting…" : label}
    </button>
  );
}

/* =========================================================
   LOGIN
========================================================= */

export function Login({ onLogin }) {
  const nav = useNavigate();

  const [form, setForm] = useState({
    email: "",
    password: "",
    remember_me: true,
  });

  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();

    if (busy) {
      return;
    }

    const email = form.email.trim();

    if (!email || !form.password) {
      return toast.error(
        "Please enter your email and password"
      );
    }

    setBusy(true);

    try {
      const data = await apiFetch("/auth/login", {
        method: "POST",
        body: JSON.stringify({
          ...form,
          email,
        }),
      });

      if (!data?.user) {
        throw new Error(
          "Login failed. Please try again."
        );
      }

      onLogin(data.user);

      toast.success(
        `Welcome back, ${
          data.user.full_name || data.user.email
        }`
      );

      const isAdmin = [
        "owner",
        "admin",
        "staff",
      ].includes(data.user.role);

      nav(isAdmin ? "/admin" : "/account");
    } catch (err) {
      toast.error(
        err.message || "Unable to sign in"
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="auth-page">
      <div className="auth-card">
        <p className="eyebrow">Welcome back</p>

        <h1>
          Sign <i>in.</i>
        </h1>

        <p className="sub">
          Access your account, orders and wishlist.
        </p>

        <div className="social-row">
          <GoogleButton />
          <FacebookButton />
        </div>

        <div className="divider">
          <span>or with email</span>
        </div>

        <form onSubmit={submit}>
          <label>
            Email
            <input
              type="email"
              required
              autoComplete="email"
              value={form.email}
              onChange={(e) =>
                setForm({
                  ...form,
                  email: e.target.value,
                })
              }
              data-testid="login-email"
            />
          </label>

          <label>
            Password
            <input
              type="password"
              required
              autoComplete="current-password"
              value={form.password}
              onChange={(e) =>
                setForm({
                  ...form,
                  password: e.target.value,
                })
              }
              data-testid="login-password"
            />
          </label>

          <div className="auth-row">
            <label className="remember">
              <input
                type="checkbox"
                checked={form.remember_me}
                onChange={(e) =>
                  setForm({
                    ...form,
                    remember_me: e.target.checked,
                  })
                }
                data-testid="login-remember"
              />

              Remember me
            </label>

            <Link
              to="/forgot-password"
              data-testid="forgot-password-link"
            >
              Forgot password?
            </Link>
          </div>

          <button
            type="submit"
            className="button button-dark full"
            disabled={busy}
            data-testid="login-submit"
          >
            {busy ? "Signing in…" : "Sign in"}
            <ArrowRight size={16} />
          </button>
        </form>

        <p className="auth-foot">
          New here?{" "}
          <Link
            to="/signup"
            data-testid="link-signup"
          >
            Create an account
          </Link>
        </p>
      </div>
    </main>
  );
}

/* =========================================================
   SIGNUP + EMAIL OTP VERIFICATION
========================================================= */

export function Signup({ onLogin }) {
  const nav = useNavigate();

  const [form, setForm] = useState({
    full_name: "",
    email: "",
    password: "",
    confirm: "",
  });

  const [busy, setBusy] = useState(false);
  const [otpBusy, setOtpBusy] = useState(false);
  const [resendBusy, setResendBusy] = useState(false);
  const [otpStep, setOtpStep] = useState(false);

  const [otp, setOtp] = useState([
    "",
    "",
    "",
    "",
    "",
    "",
  ]);

  const [countdown, setCountdown] = useState(60);
  const otpRefs = useRef([]);

  useEffect(() => {
    if (!otpStep || countdown <= 0) {
      return;
    }

    const timer = setInterval(() => {
      setCountdown((value) =>
        Math.max(0, value - 1)
      );
    }, 1000);

    return () => clearInterval(timer);
  }, [otpStep, countdown]);

  const submit = async (e) => {
    e.preventDefault();

    if (busy) {
      return;
    }

    const fullName = form.full_name.trim();
    const email = form.email.trim();

    if (!fullName) {
      return toast.error(
        "Please enter your full name"
      );
    }

    if (!email) {
      return toast.error(
        "Please enter your email address"
      );
    }

    if (form.password !== form.confirm) {
      return toast.error("Passwords don't match");
    }

    if (form.password.length < 8) {
      return toast.error(
        "Password must be at least 8 characters"
      );
    }

    setBusy(true);

    try {
      await apiFetch("/auth/signup", {
        method: "POST",
        body: JSON.stringify({
          full_name: fullName,
          email,
          password: form.password,
        }),
      });

      setForm((current) => ({
        ...current,
        full_name: fullName,
        email,
      }));

      setOtpStep(true);
      setCountdown(60);
      setOtp([
        "",
        "",
        "",
        "",
        "",
        "",
      ]);

      toast.success(
        "Verification code sent to your email"
      );

      setTimeout(() => {
        otpRefs.current[0]?.focus();
      }, 100);
    } catch (err) {
      toast.error(
        err.message ||
          "Unable to create your account"
      );
    } finally {
      setBusy(false);
    }
  };

  const handleOtpChange = (index, value) => {
    const clean = value.replace(/\D/g, "");

    if (!clean) {
      const next = [...otp];
      next[index] = "";
      setOtp(next);
      return;
    }

    /*
      Supports both:
      - entering one digit at a time
      - pasting the complete 6-digit OTP
    */
    if (clean.length > 1) {
      const digits = clean
        .slice(0, 6)
        .split("");

      const next = [
        "",
        "",
        "",
        "",
        "",
        "",
      ];

      digits.forEach((digit, i) => {
        next[i] = digit;
      });

      setOtp(next);

      const focusIndex = Math.min(
        digits.length,
        5
      );

      setTimeout(() => {
        otpRefs.current[focusIndex]?.focus();
      }, 0);

      return;
    }

    const next = [...otp];
    next[index] = clean[0];
    setOtp(next);

    if (index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (
      e.key === "Backspace" &&
      !otp[index] &&
      index > 0
    ) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();

    if (otpBusy) {
      return;
    }

    const code = otp.join("");

    if (code.length !== 6) {
      return toast.error(
        "Please enter the complete 6-digit code"
      );
    }

    setOtpBusy(true);

    try {
      await apiFetch(
        "/auth/verify-email-otp",
        {
          method: "POST",
          body: JSON.stringify({
            email: form.email.trim(),
            otp: code,
          }),
        }
      );

      toast.success(
        "Email verified successfully"
      );

      nav("/login");
    } catch (err) {
      toast.error(
        err.message ||
          "Unable to verify your email"
      );
    } finally {
      setOtpBusy(false);
    }
  };

  const resendOtp = async () => {
    if (countdown > 0 || resendBusy) {
      return;
    }

    setResendBusy(true);

    try {
      await apiFetch(
        "/auth/send-verification-otp",
        {
          method: "POST",
          body: JSON.stringify({
            email: form.email.trim(),
          }),
        }
      );

      setOtp([
        "",
        "",
        "",
        "",
        "",
        "",
      ]);

      setCountdown(60);

      toast.success(
        "A new verification code has been sent"
      );

      setTimeout(() => {
        otpRefs.current[0]?.focus();
      }, 100);
    } catch (err) {
      toast.error(
        err.message ||
          "Unable to resend the code"
      );
    } finally {
      setResendBusy(false);
    }
  };

  if (otpStep) {
    return (
      <main className="auth-page">
        <div className="auth-card">
          <p className="eyebrow">
            Email verification
          </p>

          <h1>
            Verify your <i>email.</i>
          </h1>

          <p className="sub">
            We've sent a 6-digit verification
            code to
            <br />
            <strong className="otp-email">
              {form.email}
            </strong>
          </p>

          <form onSubmit={verifyOtp}>
            <div className="otp-input-row">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => {
                    otpRefs.current[index] =
                      el;
                  }}
                  className="otp-input"
                  type="text"
                  inputMode="numeric"
                  autoComplete={
                    index === 0
                      ? "one-time-code"
                      : "off"
                  }
                  maxLength={1}
                  value={digit}
                  onChange={(e) =>
                    handleOtpChange(
                      index,
                      e.target.value
                    )
                  }
                  onKeyDown={(e) =>
                    handleOtpKeyDown(
                      index,
                      e
                    )
                  }
                  aria-label={`Verification digit ${
                    index + 1
                  }`}
                />
              ))}
            </div>

            <button
              type="submit"
              className="button button-dark full"
              disabled={otpBusy}
              data-testid="verify-otp-submit"
            >
              {otpBusy
                ? "Verifying…"
                : "Verify email"}
              <ArrowRight size={16} />
            </button>
          </form>

          <div className="otp-resend">
            {countdown > 0 ? (
              <p>
                Didn't receive the code?{" "}
                <span>
                  Resend in 00:
                  {String(countdown).padStart(
                    2,
                    "0"
                  )}
                </span>
              </p>
            ) : (
              <p>
                Didn't receive the code?{" "}
                <button
                  type="button"
                  className="otp-resend-button"
                  onClick={resendOtp}
                  disabled={resendBusy}
                >
                  {resendBusy
                    ? "Sending…"
                    : "Resend OTP"}
                </button>
              </p>
            )}
          </div>

          <button
            type="button"
            className="otp-change-email"
            onClick={() => {
              setOtpStep(false);
              setOtp([
                "",
                "",
                "",
                "",
                "",
                "",
              ]);
            }}
          >
            ← Change email
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="auth-page">
      <div className="auth-card">
        <p className="eyebrow">
          Create your account
        </p>

        <h1>
          Join the <i>atelier.</i>
        </h1>

        <p className="sub">
          Save your pieces, track orders and
          shop with ease.
        </p>

        <div className="social-row">
          <GoogleButton label="Sign up with Google" />
          <FacebookButton label="Sign up with Facebook" />
        </div>

        <div className="divider">
          <span>or with email</span>
        </div>

        <form onSubmit={submit}>
          <label>
            Full name
            <input
              required
              autoComplete="name"
              value={form.full_name}
              onChange={(e) =>
                setForm({
                  ...form,
                  full_name: e.target.value,
                })
              }
              data-testid="signup-name"
            />
          </label>

          <label>
            Email
            <input
              type="email"
              required
              autoComplete="email"
              value={form.email}
              onChange={(e) =>
                setForm({
                  ...form,
                  email: e.target.value,
                })
              }
              data-testid="signup-email"
            />
          </label>

          <label>
            Password
            <input
              type="password"
              required
              minLength={8}
              autoComplete="new-password"
              value={form.password}
              onChange={(e) =>
                setForm({
                  ...form,
                  password: e.target.value,
                })
              }
              data-testid="signup-password"
            />
          </label>

          <label>
            Confirm password
            <input
              type="password"
              required
              minLength={8}
              autoComplete="new-password"
              value={form.confirm}
              onChange={(e) =>
                setForm({
                  ...form,
                  confirm: e.target.value,
                })
              }
              data-testid="signup-confirm"
            />
          </label>

          <button
            type="submit"
            className="button button-dark full"
            disabled={busy}
            data-testid="signup-submit"
          >
            {busy
              ? "Creating…"
              : "Create account"}
            <ArrowRight size={16} />
          </button>
        </form>

        <p className="auth-foot">
          Already have an account?{" "}
          <Link
            to="/login"
            data-testid="link-login"
          >
            Sign in
          </Link>
        </p>
      </div>
    </main>
  );
}

/* =========================================================
   FORGOT PASSWORD
========================================================= */

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();

    if (busy) {
      return;
    }

    const cleanEmail = email.trim();

    if (!cleanEmail) {
      return toast.error(
        "Please enter your email address"
      );
    }

    setBusy(true);

    try {
      /*
        The backend intentionally gives the same
        user-facing result whether or not the email
        exists, preventing account enumeration.
      */
      await apiFetch(
        "/auth/forgot-password",
        {
          method: "POST",
          body: JSON.stringify({
            email: cleanEmail,
          }),
        }
      );
    } catch {
      /*
        Keep the response intentionally generic.
      */
    } finally {
      setEmail(cleanEmail);
      setDone(true);
      setBusy(false);
    }
  };

  return (
    <main className="auth-page">
      <div className="auth-card">
        <p className="eyebrow">
          Reset password
        </p>

        <h1>
          Forgot <i>password?</i>
        </h1>

        {done ? (
          <p className="sub">
            If an account exists for{" "}
            <b>{email}</b>, we've sent a reset
            link. The link expires in 45 minutes.
          </p>
        ) : (
          <>
            <p className="sub">
              Enter your email and we'll send a
              secure reset link.
            </p>

            <form onSubmit={submit}>
              <label>
                Email
                <input
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) =>
                    setEmail(e.target.value)
                  }
                  data-testid="forgot-email"
                />
              </label>

              <button
                type="submit"
                className="button button-dark full"
                disabled={busy}
                data-testid="forgot-submit"
              >
                {busy
                  ? "Sending…"
                  : "Send reset link"}
                <ArrowRight size={16} />
              </button>
            </form>
          </>
        )}

        <p className="auth-foot">
          <Link to="/login">
            Back to sign in
          </Link>
        </p>
      </div>
    </main>
  );
}

/* =========================================================
   RESET PASSWORD
========================================================= */

export function ResetPassword() {
  const [sp] = useSearchParams();
  const nav = useNavigate();

  const token = sp.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();

    if (busy) {
      return;
    }

    if (!token) {
      return toast.error(
        "This password reset link is invalid or expired"
      );
    }

    if (password !== confirm) {
      return toast.error(
        "Passwords don't match"
      );
    }

    if (password.length < 8) {
      return toast.error(
        "Password must be at least 8 characters"
      );
    }

    setBusy(true);

    try {
      await apiFetch(
        "/auth/reset-password",
        {
          method: "POST",
          body: JSON.stringify({
            token,
            password,
          }),
        }
      );

      toast.success(
        "Password updated — please sign in"
      );

      nav("/login");
    } catch (err) {
      toast.error(
        err.message ||
          "Unable to update your password"
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="auth-page">
      <div className="auth-card">
        <p className="eyebrow">
          Set a new password
        </p>

        <h1>
          New <i>password.</i>
        </h1>

        <form onSubmit={submit}>
          <label>
            New password
            <input
              type="password"
              required
              minLength={8}
              autoComplete="new-password"
              value={password}
              onChange={(e) =>
                setPassword(e.target.value)
              }
              data-testid="reset-password"
            />
          </label>

          <label>
            Confirm
            <input
              type="password"
              required
              minLength={8}
              autoComplete="new-password"
              value={confirm}
              onChange={(e) =>
                setConfirm(e.target.value)
              }
              data-testid="reset-confirm"
            />
          </label>

          <button
            type="submit"
            className="button button-dark full"
            disabled={busy}
            data-testid="reset-submit"
          >
            {busy
              ? "Updating…"
              : "Update password"}
            <ArrowRight size={16} />
          </button>
        </form>
      </div>
    </main>
  );
}

/* =========================================================
   LEGACY EMAIL VERIFICATION LINK
========================================================= */

export function VerifyEmail() {
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";

  const [state, setState] =
    useState("verifying");

  useEffect(() => {
    if (!token) {
      setState("error");
      return;
    }

    apiFetch("/auth/verify-email", {
      method: "POST",
      body: JSON.stringify({ token }),
    })
      .then(() => setState("ok"))
      .catch(() => setState("error"));
  }, [token]);

  return (
    <main className="auth-page">
      <div className="auth-card">
        <p className="eyebrow">
          Email verification
        </p>

        {state === "verifying" && (
          <h1>Verifying…</h1>
        )}

        {state === "ok" && (
          <>
            <h1>
              Email <i>verified.</i>
            </h1>

            <p className="sub">
              You're all set. Welcome to GMK
              Handicrafts Emporium.
            </p>

            <Link
              to="/login"
              className="button button-dark full"
              data-testid="verify-signin"
            >
              Sign in
              <ArrowRight size={16} />
            </Link>
          </>
        )}

        {state === "error" && (
          <>
            <h1>
              Link <i>invalid.</i>
            </h1>

            <p className="sub">
              This verification link is invalid
              or has expired.
            </p>

            <Link
              to="/login"
              className="button button-dark full"
            >
              Back to sign in
              <ArrowRight size={16} />
            </Link>
          </>
        )}
      </div>
    </main>
  );
}

/* =========================================================
   GOOGLE OAUTH CALLBACK
========================================================= */

/**
 * AuthCallback — handles direct Google OAuth callback.
 */
export function AuthCallback({ onLogin }) {
  const nav = useNavigate();
  const location = useLocation();

  const [state, setState] =
    useState("working");

  useEffect(() => {
    const params = new URLSearchParams(
      location.search
    );

    const code = params.get("code");

    if (!code) {
      setState("error");
      return;
    }

    let mounted = true;

    apiFetch("/auth/google/exchange", {
      method: "POST",
      body: JSON.stringify({ code }),
    })
      .then((data) => {
        if (!mounted) {
          return;
        }

        if (!data?.user) {
          throw new Error(
            "Google authentication failed"
          );
        }

        onLogin(data.user);

        const isAdmin = [
          "owner",
          "admin",
          "staff",
        ].includes(data.user.role);

        window.history.replaceState(
          null,
          "",
          "/"
        );

        nav(
          isAdmin
            ? "/admin"
            : "/account",
          { replace: true }
        );
      })
      .catch(() => {
        if (mounted) {
          setState("error");
        }
      });

    return () => {
      mounted = false;
    };
  }, [
    location.search,
    nav,
    onLogin,
  ]);

  return (
    <main className="auth-page">
      <div className="auth-card">
        <p className="eyebrow">
          Signing you in
        </p>

        <h1>
          {state === "working"
            ? "One moment…"
            : "Something went wrong."}
        </h1>

        {state === "error" && (
          <Link
            to="/login"
            className="button button-dark full"
          >
            Back to sign in
          </Link>
        )}
      </div>
    </main>
  );
}

/* =========================================================
   CUSTOMER ACCOUNT
========================================================= */

export function Account({
  user,
  setUser,
  logout,
  wishlistIds,
  toggleWishLocal,
}) {
  const [sp] = useSearchParams();

  const activeTab =
    sp.get("tab") || "profile";

  const [orders, setOrders] = useState([]);
  const [wishProducts, setWishProducts] =
    useState([]);
  const [addresses, setAddresses] =
    useState([]);

  const [ordersLoading, setOrdersLoading] =
    useState(false);

  const [
    addressesLoading,
    setAddressesLoading,
  ] = useState(false);

  const [
    wishlistLoading,
    setWishlistLoading,
  ] = useState(false);

  /* =======================================================
     ADDRESS EDITING
  ======================================================= */

  const [editingAddress, setEditingAddress] =
    useState(null);

  /* =======================================================
     PROFILE EDITING
  ======================================================= */

  const [editingName, setEditingName] =
    useState(false);

  const [nameValue, setNameValue] =
    useState(user?.full_name || "");

  const [nameBusy, setNameBusy] =
    useState(false);

  const [changingEmail, setChangingEmail] =
    useState(false);

  const [emailValue, setEmailValue] =
    useState("");

  const [emailOtp, setEmailOtp] =
    useState([
      "",
      "",
      "",
      "",
      "",
      "",
    ]);

  const [emailOtpStep, setEmailOtpStep] =
    useState(false);

  const [emailBusy, setEmailBusy] =
    useState(false);

  const [emailVerifyBusy, setEmailVerifyBusy] =
    useState(false);

  const [emailResendBusy, setEmailResendBusy] =
    useState(false);

  const [emailCountdown, setEmailCountdown] =
    useState(0);

  const emailOtpRefs = useRef([]);

  /* =======================================================
     USER SYNC
  ======================================================= */

  useEffect(() => {
    setNameValue(user?.full_name || "");
  }, [user?.full_name]);

  /* =======================================================
     EMAIL OTP COUNTDOWN
  ======================================================= */

  useEffect(() => {
    if (
      !emailOtpStep ||
      emailCountdown <= 0
    ) {
      return;
    }

    const timer = setInterval(() => {
      setEmailCountdown((value) =>
        Math.max(0, value - 1)
      );
    }, 1000);

    return () => clearInterval(timer);
  }, [
    emailOtpStep,
    emailCountdown,
  ]);

  /* =======================================================
     LOAD ACCOUNT DATA
  ======================================================= */

  useEffect(() => {
    if (!user) {
      setOrders([]);
      setAddresses([]);
      setWishProducts([]);
      return;
    }

    let mounted = true;

    setOrdersLoading(true);
    setAddressesLoading(true);

    apiFetch("/orders/mine")
      .then((data) => {
        if (!mounted) {
          return;
        }

        setOrders(
          Array.isArray(data)
            ? data
            : []
        );
      })
      .catch(() => {
        if (mounted) {
          setOrders([]);
        }
      })
      .finally(() => {
        if (mounted) {
          setOrdersLoading(false);
        }
      });

    apiFetch("/addresses")
      .then((data) => {
        if (!mounted) {
          return;
        }

        setAddresses(
          Array.isArray(data)
            ? data
            : []
        );
      })
      .catch(() => {
        if (mounted) {
          setAddresses([]);
        }
      })
      .finally(() => {
        if (mounted) {
          setAddressesLoading(false);
        }
      });

    if (!wishlistIds?.length) {
      setWishProducts([]);
      setWishlistLoading(false);
    } else {
      setWishlistLoading(true);

      apiFetch("/products?limit=200")
        .then((all) => {
          if (!mounted) {
            return;
          }

          const products = Array.isArray(all)
            ? all
            : [];

          setWishProducts(
            products.filter((product) =>
              wishlistIds.includes(product.id)
            )
          );
        })
        .catch(() => {
          if (mounted) {
            setWishProducts([]);
          }
        })
        .finally(() => {
          if (mounted) {
            setWishlistLoading(false);
          }
        });
    }

    return () => {
      mounted = false;
    };
  }, [user, wishlistIds]);

  /* =======================================================
     PROFILE — UPDATE NAME
  ======================================================= */

  const saveName = async (e) => {
    e.preventDefault();

    if (nameBusy) {
      return;
    }

    const cleanName = nameValue
      .trim()
      .replace(/\s+/g, " ");

    if (!cleanName) {
      return toast.error(
        "Please enter your full name"
      );
    }

    if (cleanName.length > 120) {
      return toast.error(
        "Your name is too long"
      );
    }

    setNameBusy(true);

    try {
      const data = await apiFetch(
        "/auth/profile",
        {
          method: "PUT",
          body: JSON.stringify({
            full_name: cleanName,
          }),
        }
      );

      if (!data?.user) {
        throw new Error(
          "Profile update failed"
        );
      }

      setUser(data.user);

      setNameValue(
        data.user.full_name || cleanName
      );

      setEditingName(false);

      toast.success(
        "Your name has been updated"
      );
    } catch (err) {
      toast.error(
        err.message ||
          "Unable to update your name"
      );
    } finally {
      setNameBusy(false);
    }
  };

  const cancelNameEdit = () => {
    setNameValue(user?.full_name || "");
    setEditingName(false);
  };

  /* =======================================================
     EMAIL CHANGE — START
  ======================================================= */

  const startEmailChange = () => {
    setChangingEmail(true);
    setEmailOtpStep(false);
    setEmailValue("");

    setEmailOtp([
      "",
      "",
      "",
      "",
      "",
      "",
    ]);

    setEmailCountdown(0);
  };

  const cancelEmailChange = () => {
    setChangingEmail(false);
    setEmailOtpStep(false);
    setEmailValue("");

    setEmailOtp([
      "",
      "",
      "",
      "",
      "",
      "",
    ]);

    setEmailCountdown(0);
    setEmailBusy(false);
    setEmailVerifyBusy(false);
    setEmailResendBusy(false);
  };

  /* =======================================================
     EMAIL CHANGE — REQUEST OTP
  ======================================================= */

  const requestEmailOtp = async (e) => {
    e.preventDefault();

    if (emailBusy) {
      return;
    }

    const cleanEmail =
      emailValue.trim().toLowerCase();

    if (!cleanEmail) {
      return toast.error(
        "Please enter your new email address"
      );
    }

    if (
      !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(
        cleanEmail
      )
    ) {
      return toast.error(
        "Please enter a valid email address"
      );
    }

    if (
      cleanEmail ===
      (user?.email || "")
        .trim()
        .toLowerCase()
    ) {
      return toast.error(
        "Your new email must be different from your current email"
      );
    }

    setEmailBusy(true);

    try {
      await apiFetch(
        "/auth/email-change/request",
        {
          method: "POST",
          body: JSON.stringify({
            new_email: cleanEmail,
          }),
        }
      );

      setEmailValue(cleanEmail);

      setEmailOtp([
        "",
        "",
        "",
        "",
        "",
        "",
      ]);

      setEmailOtpStep(true);
      setEmailCountdown(60);

      toast.success(
        "Verification code sent to your new email"
      );

      setTimeout(() => {
        emailOtpRefs.current[0]?.focus();
      }, 100);
    } catch (err) {
      toast.error(
        err.message ||
          "Unable to send the verification code"
      );
    } finally {
      setEmailBusy(false);
    }
  };

  /* =======================================================
     EMAIL OTP INPUT
  ======================================================= */

  const handleEmailOtpChange = (
    index,
    value
  ) => {
    const clean = value.replace(
      /\D/g,
      ""
    );

    if (!clean) {
      const next = [...emailOtp];

      next[index] = "";

      setEmailOtp(next);

      return;
    }

    if (clean.length > 1) {
      const digits = clean
        .slice(0, 6)
        .split("");

      const next = [
        "",
        "",
        "",
        "",
        "",
        "",
      ];

      digits.forEach((digit, i) => {
        next[i] = digit;
      });

      setEmailOtp(next);

      const focusIndex = Math.min(
        digits.length,
        5
      );

      setTimeout(() => {
        emailOtpRefs.current[
          focusIndex
        ]?.focus();
      }, 0);

      return;
    }

    const next = [...emailOtp];

    next[index] = clean[0];

    setEmailOtp(next);

    if (index < 5) {
      emailOtpRefs.current[
        index + 1
      ]?.focus();
    }
  };

  const handleEmailOtpKeyDown = (
    index,
    e
  ) => {
    if (
      e.key === "Backspace" &&
      !emailOtp[index] &&
      index > 0
    ) {
      emailOtpRefs.current[
        index - 1
      ]?.focus();
    }
  };

  /* =======================================================
     EMAIL CHANGE — VERIFY
  ======================================================= */

  const verifyEmailChange = async (
    e
  ) => {
    e.preventDefault();

    if (emailVerifyBusy) {
      return;
    }

    const code =
      emailOtp.join("");

    if (code.length !== 6) {
      return toast.error(
        "Please enter the complete 6-digit verification code"
      );
    }

    setEmailVerifyBusy(true);

    try {
      const data = await apiFetch(
        "/auth/email-change/verify",
        {
          method: "POST",
          body: JSON.stringify({
            new_email:
              emailValue.trim().toLowerCase(),
            otp: code,
          }),
        }
      );

      if (!data?.user) {
        throw new Error(
          "Email change failed"
        );
      }

      setUser(data.user);

      setChangingEmail(false);
      setEmailOtpStep(false);
      setEmailValue("");

      setEmailOtp([
        "",
        "",
        "",
        "",
        "",
        "",
      ]);

      setEmailCountdown(0);

      toast.success(
        "Your email address has been changed successfully"
      );
    } catch (err) {
      toast.error(
        err.message ||
          "Unable to verify the new email address"
      );
    } finally {
      setEmailVerifyBusy(false);
    }
  };

  /* =======================================================
     EMAIL CHANGE — RESEND
  ======================================================= */

  const resendEmailOtp = async () => {
    if (
      emailCountdown > 0 ||
      emailResendBusy
    ) {
      return;
    }

    setEmailResendBusy(true);

    try {
      await apiFetch(
        "/auth/email-change/request",
        {
          method: "POST",
          body: JSON.stringify({
            new_email:
              emailValue.trim().toLowerCase(),
          }),
        }
      );

      setEmailOtp([
        "",
        "",
        "",
        "",
        "",
        "",
      ]);

      setEmailCountdown(60);

      toast.success(
        "A new verification code has been sent"
      );

      setTimeout(() => {
        emailOtpRefs.current[0]?.focus();
      }, 100);
    } catch (err) {
      toast.error(
        err.message ||
          "Unable to resend the verification code"
      );
    } finally {
      setEmailResendBusy(false);
    }
  };

  /* =======================================================
     ADDRESS — SAVE
  ======================================================= */

  const handleAddressSaved = (saved) => {
    if (!saved) {
      return;
    }

    setAddresses((current) => {
      const savedId =
        saved.address_id;

      const normalized = saved.is_default
        ? current.map((address) => ({
            ...address,
            is_default:
              address.address_id ===
              savedId,
          }))
        : current;

      const existingIndex =
        normalized.findIndex(
          (address) =>
            address.address_id ===
            savedId
        );

      if (existingIndex !== -1) {
        return normalized.map(
          (address, index) =>
            index === existingIndex
              ? saved
              : address
        );
      }

      return [...normalized, saved];
    });

    setEditingAddress(null);
  };

  const startAddressEdit = (address) => {
    setEditingAddress(address);
  };

  const cancelAddressEdit = () => {
    setEditingAddress(null);
  };

  /* =======================================================
     ADDRESS — MAKE DEFAULT
  ======================================================= */

  const makeDefaultAddress = async (
    addressId
  ) => {
    try {
      const data = await apiFetch(
        `/addresses/${addressId}/default`,
        {
          method: "POST",
        }
      );

      const savedDefault =
        data?.address;

      const activeDefaultId =
        savedDefault?.address_id ||
        addressId;

      setAddresses((current) =>
        current.map((address) => ({
          ...address,
          is_default:
            address.address_id ===
            activeDefaultId,
        }))
      );

      setEditingAddress((current) => {
        if (
          !current ||
          current.address_id !==
            addressId
        ) {
          return current;
        }

        return {
          ...current,
          is_default: true,
        };
      });

      toast.success(
        "Default address updated"
      );
    } catch (err) {
      toast.error(
        err.message ||
          "Unable to make this address default"
      );
    }
  };

  /* =======================================================
     ADDRESS — DELETE
  ======================================================= */

  const delAddress = async (id) => {
    const addressToDelete =
      addresses.find(
        (address) =>
          address.address_id === id
      );

    if (!addressToDelete) {
      return;
    }

    const confirmed = window.confirm(
      "Are you sure you want to remove this address?"
    );

    if (!confirmed) {
      return;
    }

    try {
      await apiFetch(
        `/addresses/${id}`,
        {
          method: "DELETE",
        }
      );

      setAddresses((current) => {
        const remaining = current.filter(
          (address) =>
            address.address_id !== id
        );

        if (
          addressToDelete.is_default &&
          remaining.length > 0
        ) {
          const promotedId =
            remaining[
              remaining.length - 1
            ].address_id;

          return remaining.map(
            (address) => ({
              ...address,
              is_default:
                address.address_id ===
                promotedId,
            })
          );
        }

        return remaining;
      });

      setEditingAddress((current) =>
        current?.address_id === id
          ? null
          : current
      );

      toast.success(
        "Address removed"
      );
    } catch (err) {
      toast.error(
        err.message ||
          "Unable to remove address"
      );
    }
  };

  /* =======================================================
     ACCOUNT DISPLAY HELPERS
  ======================================================= */

  const firstName =
    user?.full_name
      ?.trim()
      ?.split(/\s+/)[0] ||
    "Welcome";

  const roleLabel =
    user?.role || "customer";

  const wishlistCount =
    wishlistIds?.length || 0;

  const orderCount =
    orders.length;

  const addressCount =
    addresses.length;

  /* =======================================================
     ACCOUNT UI
  ======================================================= */

  return (
    <main className="account-page">
      {/* ===================================================
          SIDEBAR
      =================================================== */}

      <aside className="account-sidebar">
        <p className="eyebrow">
          My account
        </p>

        <h1>{firstName}</h1>

        <p className="account-email">
          {user?.email}
        </p>

        <div className="account-role">
          <span className="account-role-dot" />
          <span>
            {roleLabel}
          </span>
        </div>

        <nav aria-label="Account navigation">
          <Link
            to="/account?tab=profile"
            className={
              activeTab === "profile"
                ? "active"
                : ""
            }
            data-testid="account-tab-profile"
          >
            <UserRound size={15} />
            <span>Profile</span>
          </Link>

          <Link
            to="/account?tab=orders"
            className={
              activeTab === "orders"
                ? "active"
                : ""
            }
            data-testid="account-tab-orders"
          >
            <Package size={15} />
            <span>Orders</span>

            {orderCount > 0 && (
              <span className="account-nav-count">
                {orderCount}
              </span>
            )}
          </Link>

          <Link
            to="/account?tab=wishlist"
            className={
              activeTab === "wishlist"
                ? "active"
                : ""
            }
            data-testid="account-tab-wishlist"
          >
            <Heart size={15} />
            <span>Wishlist</span>

            {wishlistCount > 0 && (
              <span className="account-nav-count">
                {wishlistCount}
              </span>
            )}
          </Link>

          <Link
            to="/account?tab=addresses"
            className={
              activeTab === "addresses"
                ? "active"
                : ""
            }
            data-testid="account-tab-addresses"
          >
            <MapPin size={15} />
            <span>Addresses</span>

            {addressCount > 0 && (
              <span className="account-nav-count">
                {addressCount}
              </span>
            )}
          </Link>

          <button
            type="button"
            onClick={logout}
            className="logout-button"
            data-testid="account-logout"
          >
            <LogOut size={15} />
            <span>Sign out</span>
          </button>
        </nav>
      </aside>

      {/* ===================================================
          MAIN ACCOUNT CONTENT
      =================================================== */}

      <section className="account-body">
        {/* =================================================
            PROFILE
        ================================================= */}

        {activeTab === "profile" && (
          <div>
            <div className="account-section-heading">
              <div>
                <p className="account-kicker">
                  Personal details
                </p>

                <h2>Profile</h2>

                <p className="account-section-sub">
                  Manage your personal account
                  information.
                </p>
              </div>
            </div>

            <div className="profile-dl">
              {/* NAME */}

              <div className="profile-field profile-field-primary">
                <dt>Name</dt>

                {!editingName ? (
                  <dd>
                    <span className="profile-value">
                      {user?.full_name || "—"}
                    </span>

                    <button
                      type="button"
                      className="link"
                      onClick={() => {
                        setNameValue(
                          user?.full_name || ""
                        );

                        setEditingName(true);
                      }}
                    >
                      Edit
                    </button>
                  </dd>
                ) : (
                  <form
                    onSubmit={saveName}
                    className="profile-edit-form"
                  >
                    <input
                      type="text"
                      value={nameValue}
                      maxLength={120}
                      autoComplete="name"
                      autoFocus
                      onChange={(e) =>
                        setNameValue(
                          e.target.value
                        )
                      }
                      aria-label="Full name"
                    />

                    <div className="profile-edit-actions">
                      <button
                        type="submit"
                        className="button button-dark"
                        disabled={nameBusy}
                      >
                        {nameBusy
                          ? "Saving…"
                          : "Save"}
                      </button>

                      <button
                        type="button"
                        className="link"
                        onClick={
                          cancelNameEdit
                        }
                        disabled={nameBusy}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                )}
              </div>

              {/* EMAIL */}

              <div className="profile-field">
                <dt>Email</dt>

                {!changingEmail ? (
                  <dd>
                    <span className="profile-value profile-email-value">
                      {user?.email}

                      {user?.email_verified ? (
                        <span className="verified-badge">
                          <span className="verified-icon">
                            ✓
                          </span>
                          Verified
                        </span>
                      ) : (
                        <span className="pill-warn">
                          Unverified
                        </span>
                      )}
                    </span>

                    <button
                      type="button"
                      className="link"
                      onClick={
                        startEmailChange
                      }
                    >
                      Change email
                    </button>
                  </dd>
                ) : !emailOtpStep ? (
                  <form
                    onSubmit={
                      requestEmailOtp
                    }
                    className="profile-edit-form"
                  >
                    <input
                      type="email"
                      value={emailValue}
                      autoComplete="email"
                      autoFocus
                      placeholder="Enter your new email"
                      onChange={(e) =>
                        setEmailValue(
                          e.target.value
                        )
                      }
                      aria-label="New email address"
                    />

                    <div className="profile-edit-actions">
                      <button
                        type="submit"
                        className="button button-dark"
                        disabled={emailBusy}
                      >
                        {emailBusy
                          ? "Sending…"
                          : "Send code"}
                      </button>

                      <button
                        type="button"
                        className="link"
                        onClick={
                          cancelEmailChange
                        }
                        disabled={emailBusy}
                      >
                        Cancel
                      </button>
                    </div>

                    <p className="profile-help">
                      We'll send a 6-digit
                      verification code to your
                      new email address.
                    </p>
                  </form>
                ) : (
                  <form
                    onSubmit={
                      verifyEmailChange
                    }
                    className="profile-edit-form"
                  >
                    <p className="profile-help">
                      Enter the 6-digit code sent
                      to{" "}
                      <strong>
                        {emailValue}
                      </strong>
                      .
                    </p>

                    <div className="otp-input-row">
                      {emailOtp.map(
                        (digit, index) => (
                          <input
                            key={index}
                            ref={(el) => {
                              emailOtpRefs.current[
                                index
                              ] = el;
                            }}
                            className="otp-input"
                            type="text"
                            inputMode="numeric"
                            autoComplete={
                              index === 0
                                ? "one-time-code"
                                : "off"
                            }
                            maxLength={1}
                            value={digit}
                            onChange={(e) =>
                              handleEmailOtpChange(
                                index,
                                e.target.value
                              )
                            }
                            onKeyDown={(e) =>
                              handleEmailOtpKeyDown(
                                index,
                                e
                              )
                            }
                            aria-label={`Email verification digit ${
                              index + 1
                            }`}
                          />
                        )
                      )}
                    </div>

                    <button
                      type="submit"
                      className="button button-dark"
                      disabled={
                        emailVerifyBusy
                      }
                    >
                      {emailVerifyBusy
                        ? "Verifying…"
                        : "Verify & change email"}
                    </button>

                    <div className="profile-edit-actions">
                      {emailCountdown >
                      0 ? (
                        <span className="profile-help profile-resend-timer">
                          Resend code in 00:
                          {String(
                            emailCountdown
                          ).padStart(2, "0")}
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="link"
                          onClick={
                            resendEmailOtp
                          }
                          disabled={
                            emailResendBusy ||
                            emailVerifyBusy
                          }
                        >
                          {emailResendBusy
                            ? "Sending…"
                            : "Resend code"}
                        </button>
                      )}

                      <button
                        type="button"
                        className="link"
                        onClick={
                          cancelEmailChange
                        }
                        disabled={
                          emailVerifyBusy ||
                          emailResendBusy
                        }
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                )}
              </div>

              {/* ACCOUNT ROLE */}

              <div className="profile-field">
                <dt>Account type</dt>

                <dd>
                  <span className="profile-value">
                    <span className="account-type-mark">
                      {roleLabel}
                    </span>
                  </span>
                </dd>
              </div>

              {/* SECURITY */}

              <div className="profile-field">
                <dt>Account status</dt>

                <dd>
                  <span className="profile-value">
                    <span className="status-dot" />
                    Active
                  </span>
                </dd>
              </div>
            </div>
          </div>
        )}

        {/* =================================================
            ORDERS
        ================================================= */}

        {activeTab === "orders" && (
          <div>
            <div className="account-section-heading">
              <div>
                <p className="account-kicker">
                  Your purchases
                </p>

                <h2>Orders</h2>

                <p className="account-section-sub">
                  View your recent GMK purchases
                  and order status.
                </p>
              </div>

              {orders.length > 0 && (
                <span className="account-section-count">
                  {orders.length}{" "}
                  {orders.length === 1
                    ? "order"
                    : "orders"}
                </span>
              )}
            </div>

            {ordersLoading && (
              <div className="account-empty-state account-loading-state">
                <span className="account-loading-line" />
                <span className="account-loading-line short" />
                <p>
                  Loading your orders…
                </p>
              </div>
            )}

            {!ordersLoading &&
              !orders.length && (
                <div className="account-empty-state">
                  <div className="account-empty-icon">
                    <Package size={20} />
                  </div>

                  <h3>
                    No orders yet
                  </h3>

                  <p>
                    Your purchases will appear
                    here once you place your first
                    order.
                  </p>

                  <Link
                    to="/"
                    className="button button-dark"
                  >
                    Explore the collection
                    <ArrowRight size={15} />
                  </Link>
                </div>
              )}

            {!ordersLoading &&
              orders.length > 0 && (
                <div className="orders-list">
                  {orders.map((order) => (
                    <div
                      className="order-card"
                      key={order.order_id}
                      data-testid={`order-${order.order_number}`}
                    >
                      <div className="order-main">
                        <span className="order-label">
                          Order
                        </span>

                        <b>
                          {order.order_number}
                        </b>

                        <small>
                          {order.created_at
                            ? new Date(
                                order.created_at
                              ).toLocaleDateString(
                                "en-IN",
                                {
                                  day: "numeric",
                                  month: "short",
                                  year: "numeric",
                                }
                              )
                            : "—"}
                        </small>
                      </div>

                      <div className="order-status">
                        {order.status ||
                          "Order Placed"}
                      </div>

                      <div className="order-items">
                        {order.items
                          ?.map((item) => {
                            const quantity =
                              item.quantity ||
                              1;

                            return quantity > 1
                              ? `${item.name} ×${quantity}`
                              : item.name;
                          })
                          .join(" · ") ||
                          "Order items"}
                      </div>

                      <strong>
                        {money(order.total)}
                      </strong>
                    </div>
                  ))}
                </div>
              )}
          </div>
        )}

        {/* =================================================
            WISHLIST
        ================================================= */}

        {activeTab === "wishlist" && (
          <div>
            <div className="account-section-heading">
              <div>
                <p className="account-kicker">
                  Saved for later
                </p>

                <h2>Wishlist</h2>

                <p className="account-section-sub">
                  Your collection of pieces worth
                  coming back to.
                </p>
              </div>

              {wishlistCount > 0 && (
                <span className="account-section-count">
                  {wishlistCount}{" "}
                  {wishlistCount === 1
                    ? "piece"
                    : "pieces"}
                </span>
              )}
            </div>

            {wishlistLoading && (
              <div className="account-empty-state account-loading-state">
                <span className="account-loading-line" />
                <span className="account-loading-line short" />
                <p>
                  Loading your saved pieces…
                </p>
              </div>
            )}

            {!wishlistLoading &&
              !wishProducts.length && (
                <div className="account-empty-state">
                  <div className="account-empty-icon">
                    <Heart size={20} />
                  </div>

                  <h3>
                    Your wishlist is empty
                  </h3>

                  <p>
                    Save pieces you love and
                    they'll stay here for later.
                  </p>

                  <Link
                    to="/"
                    className="button button-dark"
                  >
                    Discover handcrafted pieces
                    <ArrowRight size={15} />
                  </Link>
                </div>
              )}

            {!wishlistLoading &&
              wishProducts.length > 0 && (
                <div className="wish-grid">
                  {wishProducts.map(
                    (product) => (
                      <div
                        key={product.id}
                        className="wish-item"
                        data-testid={`wish-item-${product.id}`}
                      >
                        <Link
                          to={`/product/${
                            product.slug ||
                            product.id
                          }`}
                        >
                          <img
                            src={mainImage(
                              product
                            )}
                            alt={
                              product.name
                            }
                            loading="lazy"
                          />

                          <div>
                            <b>
                              {product.name}
                            </b>

                            <small>
                              {money(
                                product.price
                              )}
                            </small>
                          </div>
                        </Link>

                        <button
                          type="button"
                          onClick={() =>
                            toggleWishLocal(
                              product.id
                            )
                          }
                          aria-label={`Remove ${product.name} from wishlist`}
                        >
                          ×
                        </button>
                      </div>
                    )
                  )}
                </div>
              )}
          </div>
        )}

        {/* =================================================
            SAVED ADDRESSES
        ================================================= */}

        {activeTab === "addresses" && (
          <div>
            <div className="account-section-heading">
              <div>
                <p className="account-kicker">
                  Delivery details
                </p>

                <h2>
                  Saved addresses
                </h2>

                <p className="account-section-sub">
                  Manage the addresses used for
                  your deliveries.
                </p>
              </div>

              {addressCount > 0 && (
                <span className="account-section-count">
                  {addressCount}{" "}
                  {addressCount === 1
                    ? "address"
                    : "addresses"}
                </span>
              )}
            </div>

            {addressesLoading &&
              !addresses.length && (
                <div className="account-empty-state account-loading-state">
                  <span className="account-loading-line" />
                  <span className="account-loading-line short" />
                  <p>
                    Loading your addresses…
                  </p>
                </div>
              )}

            {!addressesLoading &&
              !addresses.length && (
                <div className="account-empty-state account-empty-address">
                  <div className="account-empty-icon">
                    <MapPin size={20} />
                  </div>

                  <h3>
                    No saved addresses
                  </h3>

                  <p>
                    Add your delivery address
                    below to make checkout faster.
                  </p>
                </div>
              )}

            <div className="addr-grid">
              {addresses.map((address) => (
                <div
                  className={`addr-card ${
                    address.is_default
                      ? "addr-card-default"
                      : ""
                  }`}
                  key={address.address_id}
                >
                  <div className="addr-card-top">
                    <div>
                      <span className="addr-label">
                        Delivery address
                      </span>

                      <b>
                        {address.full_name}
                      </b>
                    </div>

                    {address.is_default && (
                      <span className="addr-default-badge">
                        Default
                      </span>
                    )}
                  </div>

                  <p>
                    {address.house}
                    <br />

                    {address.area}
                    <br />

                    {address.landmark && (
                      <>
                        {address.landmark}
                        <br />
                      </>
                    )}

                    {address.town},{" "}
                    {address.state}{" "}
                    {address.pincode}
                    <br />

                    India
                    <br />

                    {address.phone}
                  </p>

                  <div className="addr-card-actions">
                    <button
                      type="button"
                      className="link"
                      onClick={() =>
                        startAddressEdit(
                          address
                        )
                      }
                      data-testid={`edit-addr-${address.address_id}`}
                    >
                      Edit
                    </button>

                    {!address.is_default && (
                      <button
                        type="button"
                        className="link"
                        onClick={() =>
                          makeDefaultAddress(
                            address.address_id
                          )
                        }
                        data-testid={`default-addr-${address.address_id}`}
                      >
                        Make default
                      </button>
                    )}

                    <button
                      type="button"
                      className="link address-remove-link"
                      onClick={() =>
                        delAddress(
                          address.address_id
                        )
                      }
                      data-testid={`delete-addr-${address.address_id}`}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="address-form-section">
              {editingAddress ? (
                <AddressForm
                  key={
                    editingAddress.address_id
                  }
                  initialAddress={
                    editingAddress
                  }
                  onSaved={
                    handleAddressSaved
                  }
                  onCancel={
                    cancelAddressEdit
                  }
                  showCancel={true}
                />
              ) : (
                <AddressForm
                  onSaved={
                    handleAddressSaved
                  }
                />
              )}
            </div>
          </div>
        )}
      </section>
    </main>
  );
}
